import axiosClient from "../axios-client";
import { fixedNumber } from "../helpers/helpers";
import { Account } from "../models/account";
import { Document, DocumentAdd, DocumentEdit } from "../models/document";
import { DocumentItemAdd } from "../models/document-item";
import { DocumentSettings } from "../models/document-settings";
import { DocumentWorkAdd } from "../models/document-work";
import { DocumentPaymentForm } from "../models/document-payment";


type DocumentForm = {
  getValues: (name: 'to_pay') => number | undefined;
  setValue: (name: 'to_pay' | 'receipt' | 'balance' | 'works_total' | 'items_total' | 'total' | 'include_vat' | 'not_include_vat' | 'vat' | 'discount' | 'discount_percentage', value: number) => void;
};



export const getDocuments = async (sortKey: string = 'id', maxRecords: string = '0', sortMethod: string = 'asc', load: string[] = [], body: object = {}, signal: AbortSignal): Promise<Document[]> => {
    const loadString = `?load=${load.join(',')}`;
    const response = await axiosClient.post(`/api/documents/search/${maxRecords}/${sortKey}/${sortMethod}${loadString}`, body, {
        signal: signal // Pass the signal to the request options
    });
    return response.data;
}

export const getOpenDocuments = async (accountId: number, signal: AbortSignal): Promise<Document[]> => {
    const response = await axiosClient.get(`/api/documents/open/${accountId}`, {
        signal: signal // Pass the signal to the request options
    });
    return response.data;
}

export const getDocumentById = async (id: number | string, load: string[] = []): Promise<Document> => {
    const loadString = `?load=${load.join(',')}`;
    const response = await axiosClient.get(`/api/documents/${id}${loadString}`);
    return response.data;
}

export const getNextDocumentNumber = async (documentSettingsId: number): Promise<number> => {
    const body = {
        document_settings: {
            id: documentSettingsId
        }
    };
    const response = await axiosClient.post(`/api/documents/next-number`, body);
    return response.data.next_document_index;
}

export const addDocument = async (document: DocumentAdd): Promise<Document> => {
    const response = await axiosClient.post(`/api/documents`, document);
    return response.data;
}

export const saveDocument = async (documentId: number, documentToSave: DocumentEdit): Promise<Document> => {
    const response = await axiosClient.patch(`/api/documents/${documentId}`, documentToSave);
    return response.data;
}



// calc functions

export function calcWorksAndItems(documentForm: DocumentForm, works: DocumentWorkAdd[], items: DocumentItemAdd[], settings?: DocumentSettings, incomes?: Account[]): void {


    if (!settings || !incomes) {
        return;
    }

    if (settings.display_works === false && settings.display_items === false) {
        return;
    }

    const {totalPriceWorks, totalPriceWorksNoVat} = calcWorks(works, incomes, settings);
    const {totalPriceItems, totalPriceItemsNoVat} = calcItems(items, incomes, settings);

    
    documentForm.setValue('works_total', fixedNumber(totalPriceWorks + totalPriceWorksNoVat));
    documentForm.setValue('items_total', fixedNumber(totalPriceItems + totalPriceItemsNoVat));


    const totalWorksAndItemsWithVat = totalPriceWorks + totalPriceItems; //total to pay included vat
    const totalWorksAndItemsNoVat = totalPriceWorksNoVat + totalPriceItemsNoVat; //total to pay not included vat

    const totalWorksAndItems = totalWorksAndItemsWithVat + totalWorksAndItemsNoVat; 

    const { vat, to_pay, rounded} = calcVatAndFinalPay(totalPriceWorks, totalPriceItems, totalWorksAndItemsNoVat, settings);


    documentForm.setValue('total', fixedNumber(totalWorksAndItems));
    documentForm.setValue('include_vat', fixedNumber(totalWorksAndItemsWithVat - rounded));
    documentForm.setValue('not_include_vat', fixedNumber(totalWorksAndItemsNoVat));
    documentForm.setValue('vat', fixedNumber(vat, 3));
    documentForm.setValue('discount', fixedNumber(rounded));
    documentForm.setValue('discount_percentage', fixedNumber((rounded / totalWorksAndItems * 100) || 0, 3));
    documentForm.setValue('to_pay', fixedNumber(to_pay));

    if (settings.type === "חשבונית מס") {
        documentForm.setValue('balance', fixedNumber(to_pay));
    }

}


export function calcWorks(works: DocumentWorkAdd[],  incomes: Account[], settings: DocumentSettings) {

    let totalPriceWorks = 0; //total works price included vat
    let totalPriceWorksNoVat = 0; //total works price not included vat

    // calc for totalPriceWorks and totalPriceWorksNoVat
    if (settings.display_works) {

        works.forEach(row => {

            const incomeCard = incomes.find(income => income.card_number === row.income_card);

            if (incomeCard && incomeCard?.vat_include === false) {
                totalPriceWorksNoVat += row.total;
            }

            else {
                totalPriceWorks += row.total;
            }

        });

    }

    return {
        totalPriceWorks,
        totalPriceWorksNoVat
    }
}



export function calcItems(items: DocumentItemAdd[],  incomes: Account[], settings: DocumentSettings) {

    let totalPriceItems = 0; //total items price included vat
    let totalPriceItemsNoVat = 0; //total items price not included vat

    // calc for totalPriceItems and totalPriceItemsNoVat
    if (settings.display_items) {

        items.forEach(row => {

            const incomeCard = incomes.find(income => income.card_number === row.income_card);

            if (incomeCard && incomeCard?.vat_include === false) {
                totalPriceItemsNoVat += row.total;
            }

            else {
                totalPriceItems += row.total;
            }

        });

    }

    return {
        totalPriceItems,
        totalPriceItemsNoVat
    }
}



export function calcVatAndFinalPay(totalPriceWorks: number, totalPriceItems: number, totalWorksAndItemsNoVat: number, settings: DocumentSettings){

    const totalWorksAndItemsWithVat = totalPriceWorks + totalPriceItems; //total to pay included vat

    let totalWorksAndItems = totalWorksAndItemsNoVat + totalWorksAndItemsWithVat; //total to pay (works and items together)

    let vat; //vat to pay
    let to_pay = 0; //total to pay + vat
    let rounded = 0;

    // calc vat 
    if (settings?.circle) {
        const vatRate = 17 / 100;
        vat = totalWorksAndItemsWithVat * vatRate;
        to_pay = Math.round(totalWorksAndItems + vat);
        let desired_total_With_Vat = (to_pay - totalWorksAndItemsNoVat) / (1 + vatRate);
        rounded = totalWorksAndItems - desired_total_With_Vat - totalWorksAndItemsNoVat;

        vat = desired_total_With_Vat * 0.17;
    }

    else {
        vat = (totalPriceWorks + totalPriceItems) * 0.17;
        to_pay = vat + totalWorksAndItems;
    }

    return {
        vat,
        to_pay,
        rounded
    }
}




export function calcKabala(documentForm: DocumentForm, payments: DocumentPaymentForm[], settings?: DocumentSettings, documentsToClose?: Document[]): void {

    if (!settings) {
        return;
    }


    let totalPricePayments = 0; //total payments price

    //calc for totalPricePayments
    payments.forEach(row => {
        totalPricePayments += row.total;
    });



    // if heshbonit mas kabala
    if (settings.type === "חשבונית מס קבלה") {

        const heshbonitPrice = documentForm.getValues('to_pay');

        if (heshbonitPrice) {
            documentForm.setValue('balance', fixedNumber(heshbonitPrice - totalPricePayments));
        }

        documentForm.setValue('receipt', totalPricePayments);

    }

    // if kabala
    if (documentsToClose) {

        let to_pay = 0; // total heshboniot price

        documentsToClose?.forEach(document => {
            to_pay += (document.balance || 0);
        });

        documentForm.setValue('to_pay', fixedNumber(to_pay));
        documentForm.setValue('receipt', totalPricePayments);
        documentForm.setValue('balance', fixedNumber(to_pay - totalPricePayments));
    }


}